import { Button, Checkbox, Spinner, SpinnerSize } from "@blueprintjs/core";

import { DataTable, FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import { UserMetierLocalitesGrid } from ".";
import { UserMetierDto, UtilisateurApi } from "../../../api";
import { BooleanColumn } from "../../../components";
import { useAuth } from "../../../contexts";
import { useApiService, useManageError, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IProfileMetiersPageProps {}

export const ProfileMetiersPage: React.FunctionComponent<IProfileMetiersPageProps> = props => {
  const { t } = useTl();
  const api = useApiService(UtilisateurApi);
  const { user } = useAuth();
  const { manageError } = useManageError();
  const { id } = useParams<{ id: string; tab: string; state: string }>();
  const iduser = React.useMemo(() => +id, [id]);

  const { data: isCalcOfUser, isFetching: isCalcOfUserLoading } = useQuery(
    ["is-calc-of-user", iduser, user.iduser],
    async () => await api.utilisateurIsCalcOfUser({ iduser, idcalc: user.iduser })
  );

  const canEdit = React.useMemo(() => user.iduser === iduser || isCalcOfUser?.value, [
    iduser,
    isCalcOfUser,
    user.iduser
  ]);

  const fetchMetiers = React.useCallback(() => api.utilisateurGetMetiers({ id: iduser }), [api, iduser]);
  const { data, isFetching, isRefetching, refetch } = useQuery(["user-metiers", iduser], fetchMetiers);

  const tableState = useGridState<UserMetierDto>({
    serverMode: false,
    enableFilter: true,
    pageSize: 50,
    availablePageSizes: [50, 100, 500, 1000],
    sortKeys: {
      nom: "ASC",
      stade: "ASC"
    },
    storageOptions: {
      columnCustomizationStorageMode: "state"
    }
  });

  React.useEffect(() => {
    if (data) {
      tableState.setData(data);
    }
  }, [data, tableState]);

  const [rowLoading, setRowLoading] = React.useState<number>();
  const toggleSelection = React.useCallback(
    async (item: UserMetierDto) => {
      try {
        setRowLoading(item.idmetier);
        await api.utilisateurSaveUserMetier({ id: iduser, UserMetierDto: item });
      } catch (e) {
        manageError(e);
      } finally {
        await refetch();
        setRowLoading(undefined);
      }
    },
    [api, iduser, manageError, refetch]
  );

  const columns = React.useMemo<IDataTableColumn<UserMetierDto>[]>(
    () => [
      {
        autoFitContent: true,
        computed: true,
        fieldName: "_toggle",
        render: (item: UserMetierDto, _, { subPanelOpened, toggleSubPanel }) => (
          <Button icon={subPanelOpened ? "chevron-down" : "chevron-right"} onClick={() => toggleSubPanel()} minimal />
        )
      },
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        render: (item: UserMetierDto, _, { subPanelOpened, toggleSubPanel }) => {
          if (rowLoading === item.idmetier) return <Spinner size={SpinnerSize.SMALL} />;
          return canEdit ? (
            <Checkbox
              checked={item.selected}
              onChange={e => {
                toggleSelection(item);
                if (subPanelOpened) toggleSubPanel();
              }}
            />
          ) : (
            <BooleanColumn value={item.selected} />
          );
        }
      },
      {
        header: () => t(ETLCodes.Stade),
        fieldName: "stade",
        textEllipsisAfterWidth: "auto",
        autoFitContent: true
      },
      {
        header: () => t(ETLCodes.Indice),
        fieldName: "code",
        textEllipsisAfterWidth: "auto",
        autoFitContent: true
      },
      {
        header: () => t(ETLCodes.Intitule),
        fieldName: "nom"
      }
    ],
    [canEdit, rowLoading, t, toggleSelection]
  );

  return (
    <FieldSet title={!!data && t(ETLCodes.NbMetiers, { nb: data.length })}>
      <DataTable
        dateFormat="dd/MM/yyyy"
        tableState={tableState}
        loading={isFetching && !isRefetching}
        columns={columns}
        filterMode="OnEnter"
        keyFieldName="idmetier"
        onOpenSubComponent={(item: UserMetierDto) => (
          <UserMetierLocalitesGrid userId={iduser} metier={item} canEdit={canEdit}></UserMetierLocalitesGrid>
        )}
      />
    </FieldSet>
  );
};

export default ProfileMetiersPage;
